import {
  Box,
  Button,
  Card,
  Code,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog, CopyButton } from '@mangovoice/mango-ui'

import type { WebhooksApi } from '../api/webhooksApi'
import {
  useDeleteAuthKeys,
  useGenerateAuthKeys,
  useGetAuthKeys,
  useRegenerateAuthKeys,
} from '../hooks/useAuthKeys'
import { brandedButtonCss } from '../utils'

export interface AuthorizationKeysSectionProps {
  api: WebhooksApi
  pbxId: number
}

type ConfirmAction = 'regenerate' | 'remove'
const CONFIRM_REGENERATE: ConfirmAction = 'regenerate'
const CONFIRM_REMOVE: ConfirmAction = 'remove'

const KEY_FIELDS = ['key', 'token', 'pid'] as const

// Maps internal field names to the ident1/ident2/ident3 labels used in API docs
const FIELD_IDENT_KEY = {
  key: 'webhooks.authKeys.formatIdent1',
  token: 'webhooks.authKeys.formatIdent2',
  pid: 'webhooks.authKeys.formatIdent3',
} as const

export const AuthorizationKeysSection = ({
  api,
  pbxId,
}: AuthorizationKeysSectionProps) => {
  const { t } = useTranslation()
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)

  const { data: keys, isLoading, isError } = useGetAuthKeys({ api, pbxId })
  const generate = useGenerateAuthKeys({ api, pbxId })
  const regenerate = useRegenerateAuthKeys({ api, pbxId })
  const remove = useDeleteAuthKeys({ api, pbxId })

  const handleConfirm = () => {
    generate.reset()
    regenerate.reset()
    remove.reset()
    if (confirmAction === 'regenerate') regenerate.mutate()
    else if (confirmAction === 'remove') remove.mutate()
    setConfirmAction(null)
  }

  const confirmTitle =
    confirmAction === 'regenerate'
      ? t('webhooks.authKeys.confirmRegenerateTitle')
      : t('webhooks.authKeys.confirmRemoveTitle')

  const confirmBody =
    confirmAction === 'regenerate'
      ? t('webhooks.authKeys.confirmRegenerateBody')
      : t('webhooks.authKeys.confirmRemoveBody')

  const combinedToken = keys ? `${keys.key}/${keys.token}/${keys.pid}` : null

  const mutationError = generate.isError
    ? t('webhooks.authKeys.failedToGenerate')
    : regenerate.isError
      ? t('webhooks.authKeys.failedToRegenerate')
      : remove.isError
        ? t('webhooks.authKeys.failedToRemove')
        : null

  return (
    <>
      <Box p={6} pt={0}>
        <Card.Root>
          <Card.Body display="flex" flexDirection="column" gap={5}>
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={4}
            >
              <Box display="flex" flexDirection="column" gap={1}>
                <Heading size="md">{t('webhooks.authKeys.title')}</Heading>
                <Text fontSize="sm" color="fg.muted">
                  {t('webhooks.authKeys.description')}
                </Text>
              </Box>
              {!isLoading && !isError && !keys && (
                <Button
                  size="sm"
                  flexShrink={0}
                  loading={generate.isPending}
                  onClick={() => {
                    regenerate.reset()
                    remove.reset()
                    generate.mutate()
                  }}
                  css={brandedButtonCss}
                >
                  {t('webhooks.authKeys.generate')}
                </Button>
              )}
            </Box>

            <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
              {t('webhooks.authKeys.confidential')}
            </Text>

            {isLoading && <Spinner size="sm" />}

            {isError && (
              <Text color="fg.error" fontSize="sm">
                {t('webhooks.authKeys.failedToLoad')}
              </Text>
            )}

            {mutationError && (
              <Text color="fg.error" fontSize="sm">
                {mutationError}
              </Text>
            )}

            {!isLoading && !isError && (
              <>
                {keys ? (
                  <Box display="flex" flexDirection="column" gap={3}>
                    {KEY_FIELDS.map((field) => (
                      <Box
                        key={field}
                        display="flex"
                        flexDirection="column"
                        gap={1}
                      >
                        <Text
                          fontSize="sm"
                          color="fg.muted"
                          fontWeight="medium"
                        >
                          {t(FIELD_IDENT_KEY[field])}
                        </Text>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Code
                            flex={1}
                            fontSize="xs"
                            px={3}
                            py={2}
                            borderRadius="md"
                            wordBreak="break-all"
                          >
                            {keys[field]}
                          </Code>
                          <CopyButton
                            value={keys[field]}
                            copyAriaLabel={t('webhooks.authKeys.copy')}
                            copiedAriaLabel={t('webhooks.authKeys.copied')}
                            size="xs"
                            variant="ghost"
                            flexShrink={0}
                            iconSize={14}
                          />
                        </Box>
                      </Box>
                    ))}

                    <Box display="flex" flexDirection="column" gap={1} pt={1}>
                      <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                        {t('webhooks.authKeys.combinedToken')}
                      </Text>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Code
                          flex={1}
                          fontSize="xs"
                          px={3}
                          py={2}
                          borderRadius="md"
                          wordBreak="break-all"
                        >
                          {combinedToken}
                        </Code>
                        <CopyButton
                          value={combinedToken ?? ''}
                          copyAriaLabel={t('webhooks.authKeys.copy')}
                          copiedAriaLabel={t('webhooks.authKeys.copied')}
                          size="xs"
                          variant="ghost"
                          flexShrink={0}
                          iconSize={14}
                        />
                      </Box>
                      <Text fontSize="xs" color="fg.subtle">
                        {t('webhooks.authKeys.combinedTokenHint')}
                      </Text>
                    </Box>

                    <Box display="flex" gap={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        loading={regenerate.isPending}
                        disabled={remove.isPending}
                        onClick={() => setConfirmAction(CONFIRM_REGENERATE)}
                      >
                        {t('webhooks.authKeys.regenerate')}
                      </Button>
                      <Button
                        size="sm"
                        colorPalette="error"
                        variant="outline"
                        loading={remove.isPending}
                        disabled={regenerate.isPending}
                        onClick={() => setConfirmAction(CONFIRM_REMOVE)}
                      >
                        {t('webhooks.authKeys.remove')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="fg.muted">
                    {t('webhooks.authKeys.noKeys')}
                  </Text>
                )}
              </>
            )}

            <Text fontSize="sm">
              <a
                href="https://apidocs.mangovoice.com"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: 'var(--chakra-colors-fg-muted)',
                  textDecoration: 'underline',
                }}
              >
                {t('webhooks.authKeys.apiDocsLabel')}
              </a>
            </Text>
          </Card.Body>
        </Card.Root>
      </Box>

      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        title={confirmTitle}
        description={confirmBody}
        confirmLabel={t('webhooks.authKeys.confirmProceed')}
        cancelLabel={t('webhooks.authKeys.confirmCancel')}
        variant="destructive"
        maxWidth="480px"
        onConfirm={handleConfirm}
      />
    </>
  )
}
